import { useCallback, useEffect, useRef } from 'react';

export const EXTRA_ACTIVITY_HOVER_CLEAR_DELAY_MS = 120;

export const useExtraActivityHoverRetention = ({
	activeItemId,
	clearDelayMs = EXTRA_ACTIVITY_HOVER_CLEAR_DELAY_MS,
	onClearActiveItem,
}) => {
	const clearTimeoutRef = useRef(null);
	const isExtraActivityInteractionActiveRef = useRef(false);
	const isClearRequestedRef = useRef(false);
	const clearRequestedItemIdRef = useRef(null);

	const clearPendingTimeout = useCallback(() => {
		if (!clearTimeoutRef.current) return;

		window.clearTimeout(clearTimeoutRef.current);
		clearTimeoutRef.current = null;
	}, []);

	const resetPendingClear = useCallback(() => {
		isClearRequestedRef.current = false;
		clearRequestedItemIdRef.current = null;
		clearPendingTimeout();
	}, [clearPendingTimeout]);

	const clearRequestedItem = useCallback(() => {
		const itemId = clearRequestedItemIdRef.current;
		resetPendingClear();
		onClearActiveItem?.(itemId);
	}, [onClearActiveItem, resetPendingClear]);

	const requestClearActiveItem = useCallback(() => {
		isClearRequestedRef.current = true;
		clearRequestedItemIdRef.current = activeItemId ?? null;
		clearPendingTimeout();

		clearTimeoutRef.current = window.setTimeout(() => {
			clearTimeoutRef.current = null;

			if (!isExtraActivityInteractionActiveRef.current) {
				clearRequestedItem();
			}
		}, clearDelayMs);
	}, [activeItemId, clearDelayMs, clearPendingTimeout, clearRequestedItem]);

	const handleExtraActivityInteractionChange = useCallback(
		(isActive) => {
			isExtraActivityInteractionActiveRef.current = isActive;

			if (isActive) {
				clearPendingTimeout();
				return;
			}

			if (isClearRequestedRef.current) {
				clearRequestedItem();
			}
		},
		[clearPendingTimeout, clearRequestedItem]
	);

	useEffect(() => {
		resetPendingClear();
	}, [activeItemId, resetPendingClear]);

	useEffect(() => () => clearPendingTimeout(), [clearPendingTimeout]);

	return {
		handleExtraActivityInteractionChange,
		requestClearActiveItem,
		resetPendingClear,
	};
};
